import React, { useState, useEffect, ReactNode } from 'react';
import { Card } from '../components/ui/Card';
import { useTheme } from '../hooks/useTheme';
import { SunIcon } from '../components/icons/SunIcon';
import { MoonIcon } from '../components/icons/MoonIcon';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PhotoIcon } from '../components/icons/PhotoIcon';
import { EnvelopeIcon } from '../components/icons/EnvelopeIcon';
import { HelpIcon } from '../components/icons/HelpIcon';
import { ToggleSwitch } from '../components/ui/ToggleSwitch';
import { useNotifications } from '../hooks/useNotifications';
import { API_BASE_URL } from '../constants';

// Accordion component for the manual
const AccordionItem: React.FC<{ title: string; children: ReactNode; defaultOpen?: boolean }> = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border-b border-gray-200 dark:border-dark-tertiary last:border-b-0">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center p-4 text-left hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors"
                aria-expanded={isOpen}
            >
                <span className="font-semibold text-gray-800 dark:text-dark-text-primary">{title}</span>
                <span className={`material-symbols-outlined transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                    expand_more
                </span>
            </button>
            {isOpen && (
                <div className="p-4 bg-gray-50 dark:bg-dark-bg">
                   <div className="space-y-3 text-gray-700 dark:text-dark-text-secondary">
                    {children}
                   </div>
                </div>
            )}
        </div>
    );
};

// Image processing function to resize and convert to a data URL.
const processImage = (file: File, options: {
    maxWidth: number;
    maxHeight: number;
}): Promise<string> => {
    return new Promise((resolve, reject) => {
        if (file.size > 5 * 1024 * 1024) { // 5MB initial limit for UX
            return reject(new Error('O arquivo original é muito grande. Por favor, use uma imagem com menos de 5MB.'));
        }

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            if (!event.target?.result) {
                return reject(new Error("Falha ao ler o arquivo."));
            }
            const img = new Image();
            img.src = event.target.result as string;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;
                
                if (width > options.maxWidth || height > options.maxHeight) {
                    const ratio = Math.min(options.maxWidth / width, options.maxHeight / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    return reject(new Error('Não foi possível obter o contexto do canvas.'));
                }
                ctx.drawImage(img, 0, 0, width, height);
                
                // Always use PNG to preserve transparency.
                const dataUrl = canvas.toDataURL('image/png');
                resolve(dataUrl);
            };
            img.onerror = () => reject(new Error('Falha ao carregar a imagem. Verifique o formato do arquivo.'));
        };
        reader.onerror = () => reject(new Error('Ocorreu um erro ao ler o arquivo.'));
    });
};


export const ConfiguracoesPage: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const { isSoundEnabled, toggleSound } = useNotifications();
  
  // State for DEV settings
  const [fullLogo, setFullLogo] = useState('');
  const [iconLogo, setIconLogo] = useState('');
  const [favicon, setFavicon] = useState('');
  const [smtp, setSmtp] = useState({ host: '', port: '', user: '', pass: '', secure: false });
  const [isSaving, setIsSaving] = useState(false);



  // Load DEV settings on mount
  useEffect(() => {
    if (user?.role === UserRole.DEV) {
      const fetchSettings = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/settings`);
          if (response.ok) {
            const settings = await response.json();
            setFavicon(settings.favicon_url || '');
            setFullLogo(settings.logo_full_url || '');
            setIconLogo(settings.logo_icon_url || '');
          }
        } catch (error) {
          console.error("Failed to fetch settings for settings page", error);
        }
      };
      
      fetchSettings();
      
      try {
        const savedSmtp = localStorage.getItem('lam-custos-smtp');
        if (savedSmtp) {
          setSmtp(JSON.parse(savedSmtp));
        }
      } catch (error) {
        console.error("Failed to load SMTP settings from localStorage", error);
      }
    }
  }, [user]);



  const handleThemeChange = (newTheme: 'light' | 'dark') => {
    setTheme(newTheme);
  };
  
  // --- NEW IMAGE UPLOAD AND SAVE LOGIC ---

  const isDataUrl = (s: string) => s.startsWith('data:');

  const uploadImage = async (type: 'logo_full' | 'logo_icon' | 'favicon', dataUrl: string): Promise<string> => {
      const response = await fetch(`${API_BASE_URL}/images/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type, dataUrl }),
      });

      if (!response.ok) {
          const responseText = await response.text();
          console.error("DEBUG: Server Error Response Text:", responseText);
          try {
              const errorData = JSON.parse(responseText);
              throw new Error(errorData.message || `Erro do servidor (Status: ${response.status})`);
          } catch (e) {
              throw new Error(`Falha de comunicação com o servidor (Status: ${response.status}). A resposta não foi JSON. Verifique o console do navegador para mais detalhes.`);
          }
      }

      const result = await response.json();
      return result.path;
  };


  const saveSetting = async (key: string, value: string) => {
      const response = await fetch(`${API_BASE_URL}/settings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, value }),
      });
      if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Falha ao salvar a configuração.' }));
          throw new Error(errorData.message);
      }
  };

  const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: 'full' | 'icon' | 'favicon') => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
          let options;
          if (type === 'full') options = { maxWidth: 250, maxHeight: 60 };
          else if (type === 'icon') options = { maxWidth: 50, maxHeight: 50 };
          else options = { maxWidth: 64, maxHeight: 64 };
          
          const processedDataUrl = await processImage(file, options);
          
          if (type === 'full') setFullLogo(processedDataUrl);
          if (type === 'icon') setIconLogo(processedDataUrl);
          if (type === 'favicon') setFavicon(processedDataUrl);

      } catch (error) {
          console.error("Error processing image:", error);
          alert(`Ocorreu um erro ao processar a imagem: ${(error as Error).message}`);
      }
  };

  const handleSaveAllImages = async () => {
      setIsSaving(true);
      try {
          let fullLogoUrlToSave = fullLogo;
          if (fullLogo && isDataUrl(fullLogo)) {
              fullLogoUrlToSave = await uploadImage('logo_full', fullLogo);
          }

          let iconLogoUrlToSave = iconLogo;
          if (iconLogo && isDataUrl(iconLogo)) {
              iconLogoUrlToSave = await uploadImage('logo_icon', iconLogo);
          }
          
          let faviconUrlToSave = favicon;
          if (favicon && isDataUrl(favicon)) {
              faviconUrlToSave = await uploadImage('favicon', favicon);
          }

          await Promise.all([
              saveSetting('logo_full_url', fullLogoUrlToSave),
              saveSetting('logo_icon_url', iconLogoUrlToSave),
              saveSetting('favicon_url', faviconUrlToSave)
          ]);
          
          alert('Imagens salvas com sucesso! A página será atualizada para aplicar as mudanças.');
          window.location.reload();
      } catch (error) {
          console.error("Error saving images:", error);
          alert(`Ocorreu um erro ao salvar as imagens: ${(error as Error).message}`);
      } finally {
          setIsSaving(false);
      }
  };

  const handleSmtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSmtp(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveSmtp = () => {
    localStorage.setItem('lam-custos-smtp', JSON.stringify(smtp));
    alert('Configurações de SMTP salvas!');
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text-primary mb-6">Configurações</h1>
      <div className="space-y-6">
        <Card title="Opções do Sistema">
          <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-gray-100 dark:bg-dark-surface rounded-md">
                  <span className="text-gray-900 dark:text-dark-text-primary font-medium">Preferências de Tema</span>
                  
                  <div className="flex items-center space-x-2 bg-gray-200 dark:bg-dark-secondary p-1 rounded-full">
                      <button 
                          onClick={() => handleThemeChange('light')} 
                          className={`p-2 rounded-full transition-colors duration-200 ${theme === 'light' ? 'bg-brand-teal text-white' : 'text-gray-500 dark:text-dark-text-secondary hover:bg-gray-300 dark:hover:bg-dark-tertiary'}`}
                          aria-pressed={theme === 'light'}
                          title="Tema Claro"
                      >
                          <SunIcon className="text-xl"/>
                      </button>
                      <button 
                          onClick={() => handleThemeChange('dark')}
                          className={`p-2 rounded-full transition-colors duration-200 ${theme === 'dark' ? 'bg-brand-teal text-white' : 'text-gray-500 dark:text-dark-text-secondary hover:bg-gray-300 dark:hover:bg-dark-tertiary'}`}
                          aria-pressed={theme === 'dark'}
                          title="Tema Escuro"
                      >
                          <MoonIcon className="text-xl"/>
                      </button>
                  </div>
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-100 dark:bg-dark-surface rounded-md">
                  <span className="text-gray-900 dark:text-dark-text-primary font-medium">Som de Notificação</span>
                  <ToggleSwitch id="notification-sound" checked={isSoundEnabled} onChange={toggleSound} />
              </div>
          </div>
        </Card>



        {user?.role === UserRole.DEV && (
          <>
            <Card title="DEV: Configurações do Sistema" titleClassName="!bg-purple-800 !text-white">
              <div className="space-y-8">
                {/* Image Settings */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center mb-4"><PhotoIcon className="text-2xl mr-2 text-brand-lime"/>Logo e Favicon do Sistema</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4 border border-dark-tertiary rounded-md">
                    <div>
                      <label htmlFor="fullLogo" className="block text-sm font-medium text-dark-text-secondary mb-2">Logo Completo</label>
                      <Input type="file" id="fullLogo" onChange={e => handleImageFileChange(e, 'full')} accept="image/png,image/jpeg,image/svg+xml,image/webp" />
                      {fullLogo && <img src={fullLogo.startsWith('data:') ? fullLogo : fullLogo} alt="Preview" className="mt-2 h-12 bg-gray-500 p-1 rounded-md" />}
                    </div>
                    <div>
                      <label htmlFor="iconLogo" className="block text-sm font-medium text-dark-text-secondary mb-2">Ícone (Menu Recolhido)</label>
                      <Input type="file" id="iconLogo" onChange={e => handleImageFileChange(e, 'icon')} accept="image/png,image/jpeg,image/svg+xml,image/webp" />
                      {iconLogo && <img src={iconLogo.startsWith('data:') ? iconLogo : iconLogo} alt="Preview" className="mt-2 h-12 w-12 bg-gray-500 p-1 rounded-md" />}
                    </div>
                     <div>
                      <label htmlFor="faviconUpload" className="block text-sm font-medium text-dark-text-secondary mb-2">Favicon</label>
                      <Input type="file" id="faviconUpload" onChange={e => handleImageFileChange(e, 'favicon')} accept="image/x-icon,image/png,image/svg+xml,image/jpeg" />
                      {favicon && <img src={favicon.startsWith('data:') ? favicon : favicon} alt="Preview" className="mt-2 h-12 w-12 bg-gray-500 p-1 rounded-md" />}
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button onClick={handleSaveAllImages} isLoading={isSaving} disabled={isSaving}>
                      {isSaving ? 'Salvando...' : 'Salvar Todas as Imagens'}
                    </Button>
                  </div>
                </div>

                {/* SMTP Settings */}
                <div>
                  <h3 className="text-lg font-semibold flex items-center mb-4"><EnvelopeIcon className="text-2xl mr-2 text-brand-lime"/>Configuração de E-mail (SMTP)</h3>
                  <div className="space-y-4 p-4 border border-dark-tertiary rounded-md">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Servidor SMTP (Host)" name="host" value={smtp.host} onChange={handleSmtpChange} placeholder="smtp.example.com" />
                      <Input label="Porta" name="port" type="number" value={smtp.port} onChange={handleSmtpChange} placeholder="587" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input label="Usuário" name="user" value={smtp.user} onChange={handleSmtpChange} placeholder="user@example.com" />
                      <Input label="Senha" name="pass" type="password" value={smtp.pass} onChange={handleSmtpChange} />
                    </div>
                    <div className="flex items-center">
                      <input type="checkbox" id="secure" name="secure" checked={smtp.secure} onChange={handleSmtpChange} className="h-4 w-4 rounded border-dark-tertiary bg-dark-secondary text-brand-teal focus:ring-brand-teal" />
                      <label htmlFor="secure" className="ml-2 block text-sm font-medium text-dark-text-secondary">Usar Conexão Segura (SSL/TLS)</label>
                    </div>
                  </div>
                   <div className="mt-4 flex justify-end">
                    <Button onClick={handleSaveSmtp}>Salvar SMTP</Button>
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}
        
        <Card title={<><HelpIcon className="mr-3 text-2xl text-brand-lime" /> Manual do Sistema</>}>
          <div className="bg-white dark:bg-dark-secondary rounded-lg overflow-hidden border border-gray-200 dark:border-dark-tertiary">
            <AccordionItem title="1. Visão Geral (Dashboard)" defaultOpen={true}>
                <p>O <strong>Dashboard</strong> é a tela inicial e oferece uma visão consolidada de todas as obras.</p>
                <ul className='list-disc pl-5 space-y-1'>
                    <li><strong>Cards de Resumo:</strong> Mostram o total de obras, custos do mês atual, lançamentos do dia e o custo total geral.</li>
                    <li><strong>Gráficos:</strong> Apresentam a distribuição de custos por categoria (CDO, CIO, etc.) e a evolução dos custos ao longo dos últimos meses.</li>
                    <li><strong>Listas Rápidas:</strong> Exibem os últimos lançamentos realizados e um resumo de obras por estado.</li>
                    <li><strong>Gerar Relatório:</strong> O botão no topo permite baixar um PDF com um resumo visual de todos os dados do dashboard.</li>
                </ul>
            </AccordionItem>
            <AccordionItem title="2. Cadastro de Empresas">
                <p>Nesta seção, você gerencia as empresas clientes para as quais as obras são realizadas.</p>
                <ol className='list-decimal pl-5 space-y-1'>
                    <li>Vá para a página <strong>Empresas</strong> no menu lateral.</li>
                    <li>Para adicionar, clique em <strong>"Adicionar Empresa"</strong>. Preencha os dados, incluindo nome, CNPJ, endereço e um logotipo (opcional).</li>
                    <li>Para visualizar ou editar, clique no card da empresa desejada. Na janela que abrir, você pode ver os detalhes ou clicar em <strong>"Editar"</strong> para fazer alterações.</li>
                </ol>
            </AccordionItem>
            <AccordionItem title="3. Cadastro de Funcionários">
                 <p>Gerencie os usuários que terão acesso ao sistema.</p>
                <ol className='list-decimal pl-5 space-y-1'>
                    <li>Acesse a página <strong>Funcionários</strong>.</li>
                    <li>Clique em <strong>"Adicionar Usuário"</strong> para criar um novo acesso. Defina o nome, e-mail, senha e o perfil:</li>
                    <li className='ml-4 list-[circle]'><strong>- Administrador:</strong> Tem acesso a todas as funcionalidades, incluindo cadastro de obras e funcionários.</li>
                    <li className='ml-4 list-[circle]'><strong>- Funcionário:</strong> Tem acesso para visualizar obras e fazer lançamentos de custos.</li>
                    <li>Use os botões de ação na lista para editar ou excluir um usuário existente.</li>
                </ol>
            </AccordionItem>
            <AccordionItem title="4. Cadastro de Obras">
                <p>Cada projeto deve ser cadastrado como uma "Obra".</p>
                <ol className='list-decimal pl-5 space-y-1'>
                    <li>Vá para a página <strong>Obras</strong>.</li>
                    <li>Clique em <strong>"Adicionar Nova Obra"</strong>.</li>
                    <li>Na janela, selecione a <strong>Empresa</strong> cliente, o <strong>Encarregado</strong> pela obra e forneça uma <strong>Descrição do Serviço</strong>.</li>
                    <li>A nova obra aparecerá na lista. O nome e a localização serão preenchidos automaticamente com base na empresa selecionada.</li>
                </ol>
            </AccordionItem>
            <AccordionItem title="5. Lançamento de Custos (CDO / CIO)">
                <p>Os custos são lançados dentro de cada obra, divididos por categorias e sub-itens.</p>
                <ol className='list-decimal pl-5 space-y-1'>
                    <li>Na página <strong>Obras</strong>, clique na obra desejada para ver seus detalhes.</li>
                    <li>Na tela de detalhes da obra, na seção "Custos Individuais", clique na categoria principal (ex: <strong>CDO</strong> ou <strong>CIO</strong>).</li>
                    <li>Você verá os sub-itens dessa categoria. Clique no sub-item onde deseja lançar a despesa (ex: "Aquisição de Ferramentas").</li>
                    <li>Na tela do sub-item, clique em <strong>"Adicionar"</strong>.</li>
                    <li>Preencha o formulário com todos os detalhes da despesa, como fornecedor, nota fiscal, valores e anexe comprovantes se necessário. Salve o lançamento.</li>
                </ol>
            </AccordionItem>
             <AccordionItem title="6. Lançamento de Custos (Despesas ADM / Tributos)">
                <p>Categorias como Despesas Administrativas e Tributos têm um fluxo de lançamento simplificado.</p>
                 <ol className='list-decimal pl-5 space-y-1'>
                    <li>Na tela de detalhes da obra, clique na categoria desejada (ex: <strong>Despesas com Administração</strong>).</li>
                    <li>Você verá uma tabela de lançamentos diretos. Clique em <strong>"Adicionar Lançamento"</strong>.</li>
                    <li>Preencha o formulário simplificado com o mês de referência, valor e descrição.</li>
                    <li>O lançamento será adicionado diretamente à tabela da categoria.</li>
                </ol>
            </AccordionItem>
             <AccordionItem title="7. Bloqueio de Obras e Relatórios">
                <p>Finalize uma obra ou gere relatórios para análise.</p>
                <ul className='list-disc pl-5 space-y-1'>
                    <li><strong>Bloquear Obra:</strong> Na página de detalhes da obra, o botão <strong>"Bloquear"</strong> impede novos lançamentos e edições, "fechando" o balanço da obra. Apenas quem bloqueou pode desbloquear.</li>
                    <li><strong>Gerar Relatório de Obra:</strong> Na mesma página, clique em <strong>"Imprimir"</strong>. Uma nova tela será aberta onde você pode selecionar o mês e gerar um relatório detalhado em PDF da obra.</li>
                </ul>
            </AccordionItem>
          </div>
        </Card>
      </div>
    </div>
  );
};